import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  UserPlus,
  Trash2,
  Crown,
  Loader2,
  MailWarning,
  Clock,
  X,
  ShieldAlert,
  ArrowRightLeft,
  Check,
} from 'lucide-react';
import {
  PERMISSION_SCOPES,
  DEFAULT_PERMISSIONS,
} from '@superchat/shared/constants';
import { useWorkspace } from '../lib/workspace.jsx';
import { api } from '../lib/api.js';
import { shortTime } from '../lib/format.js';

const SCOPE_LABEL = {
  chat: 'Chat',
  docs: 'Docs',
  analytics: 'Analytics',
  integration: 'Integration',
};

const SCOPE_HELP = {
  chat: 'Conversations and tickets. "Reply" also covers status changes and tags.',
  docs: 'The help centre articles the widget serves.',
  analytics: 'Volume, response times and the online-visitor list.',
  integration: 'Install snippet, public key, allowed origins and widget appearance.',
};

const LEVEL_LABEL = {
  none: 'No access',
  read: 'Read only',
  send: 'Read and reply',
  write: 'Read and edit',
};

/** One ladder of radio-like buttons. Reads as a level, not four checkboxes. */
function PermissionPicker({ scope, value, disabled, onChange }) {
  return (
    <div className="perm">
      <div className="perm__meta">
        <span className="perm__name">{SCOPE_LABEL[scope]}</span>
        <span className="perm__help">{SCOPE_HELP[scope]}</span>
      </div>
      <div className="perm__levels">
        {PERMISSION_SCOPES[scope].map((level) => (
          <button
            key={level}
            type="button"
            className={`perm__level${value === level ? ' is-active' : ''}`}
            disabled={disabled}
            aria-pressed={value === level}
            onClick={() => onChange(level)}
          >
            {LEVEL_LABEL[level]}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * One app in the invitation, with its own grant.
 *
 * Ticking the app is what opens its ladders. Sharing four apps at one blanket
 * level is exactly the mistake this screen exists to prevent — the person
 * covering your chat inbox rarely needs your integration keys as well — so the
 * permissions live inside the app that grants them, never above the list.
 */
function ShareApp({ app, selected, permissions, onToggle, onChange, disabled }) {
  return (
    <div className={`share__app${selected ? ' is-selected' : ''}`}>
      <label className="share__pick">
        <input
          type="checkbox"
          checked={selected}
          disabled={disabled}
          onChange={(event) => onToggle(event.target.checked)}
        />
        <span className="share__name">{app.name}</span>
        <span className="share__slug mono">{app.slug}</span>
      </label>

      {selected && (
        <div className="perms">
          {Object.keys(PERMISSION_SCOPES).map((scope) => (
            <PermissionPicker
              key={scope}
              scope={scope}
              value={permissions[scope]}
              disabled={disabled}
              onChange={(level) => onChange(scope, level)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Handing the app to somebody else.
 *
 * Two steps, and the password on both. The first is "who", the second is "yes,
 * really" — and between them sits the only screen that spells out what the
 * owner is giving up. Ownership cannot be edited away afterwards, so the person
 * doing it cannot undo it alone; that is worth two deliberate keystrokes.
 *
 * The challenge from step one lives in component state and nowhere else. A
 * reload therefore loses it, and the pending transfer has to be started again —
 * which is the honest behaviour, since the whole point of the challenge is that
 * only the browser that read the warning can finish the job.
 */
function TransferCard({ app, members, onDone }) {
  const candidates = useMemo(() => members.filter((m) => !m.isOwner), [members]);

  const [pending, setPending] = useState(null);
  const [toUserId, setToUserId] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const loadPending = useCallback(async () => {
    try {
      const data = await api.transfer.pending(app.id);
      setPending(data.transfer);
    } catch {
      setPending(null);
    }
  }, [app.id]);

  useEffect(() => {
    // Reset everything when the app in focus changes — a half-finished
    // handover must never carry over to a different tenant.
    setChallenge(null);
    setTarget(null);
    setPassword('');
    setToUserId('');
    setDone(null);
    setError(null);
    loadPending();
  }, [app.id, loadPending]);

  const start = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await api.transfer.start(app.id, toUserId, password);
      setChallenge(data.challenge);
      setTarget(data.to);
      setPending(data.transfer);
      // Never carried between the steps: the second step asks for it again on
      // purpose, and holding it in memory in between would defeat that.
      setPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await api.transfer.confirm(app.id, challenge, password);
      setChallenge(null);
      setPassword('');
      setPending(null);
      setDone(data);
      await onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const abandon = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.transfer.cancel(app.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setChallenge(null);
      setTarget(null);
      setPassword('');
      setPending(null);
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="card">
        <div className="card__head">
          <span className="card__title">
            <Check size={13} /> {app.name} now belongs to {done.newOwner.email}
          </span>
        </div>
        <div className="card__foot">
          You keep full access as a member, so nothing you were working on has gone
          away. Managing who else can reach this app is now theirs alone.
        </div>
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <div className="card">
        <div className="card__head">
          <span className="card__title">
            <ArrowRightLeft size={13} /> Transfer ownership
          </span>
        </div>
        <div className="card__foot">
          Ownership can only pass to someone who already has access to {app.name} —
          that acceptance is the only record that they agreed to take it on. Invite
          them first, then come back here.
        </div>
      </div>
    );
  }

  /* Step two: the warning, and the password again. */
  if (challenge && target) {
    return (
      <form className="card card--danger" onSubmit={confirm}>
        <div className="card__head">
          <span className="card__title">
            <ShieldAlert size={13} className="danger" /> Confirm the transfer
          </span>
        </div>

        <div className="transfer__warn">
          <p>
            <strong>{app.name}</strong> will belong to{' '}
            <strong>{target.name || target.email}</strong>{' '}
            <span className="mono">({target.email})</span>.
          </p>
          <ul className="transfer__list">
            <li>They gain full access to every area, permanently.</li>
            <li>They alone decide who else can reach this app.</li>
            <li>
              You stay on as a member with full access — but you can no longer invite,
              remove or rotate keys.
            </li>
            <li>Only the new owner can transfer it back. You cannot undo this yourself.</li>
          </ul>
        </div>

        <div className="card__body">
          <label className="field">
            <span className="field__label">CONFIRM WITH YOUR PASSWORD</span>
            <input
              className="field__control"
              type="password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
        </div>

        {error && <div className="login__error">{error}</div>}

        <div className="card__actions">
          <button className="button button--danger" type="submit" disabled={busy}>
            {busy && <Loader2 size={14} className="spin" />} Transfer {app.name}
          </button>
          <button type="button" className="button" onClick={abandon} disabled={busy}>
            Keep ownership
          </button>
        </div>
      </form>
    );
  }

  /* A transfer left pending by an earlier session — its challenge is gone. */
  if (pending) {
    return (
      <div className="card">
        <div className="card__head">
          <span className="card__title">
            <Clock size={13} /> Transfer waiting to be confirmed
          </span>
        </div>
        <div className="card__foot">
          A transfer of {app.name} to <span className="mono">{pending.toEmail}</span> was
          started but never confirmed. Nothing has moved. Confirming has to happen in
          the browser that started it, so from here you can either cancel it or start
          again.
        </div>
        {error && <div className="login__error">{error}</div>}
        <div className="card__actions">
          <button type="button" className="button" onClick={abandon} disabled={busy}>
            {busy && <Loader2 size={14} className="spin" />} Cancel that transfer
          </button>
        </div>
      </div>
    );
  }

  /* Step one: who, and the password. */
  return (
    <form className="card" onSubmit={start}>
      <div className="card__head">
        <span className="card__title">
          <ArrowRightLeft size={13} /> Transfer ownership
        </span>
      </div>

      <div className="card__body">
        <label className="field">
          <span className="field__label">HAND {app.name.toUpperCase()} TO</span>
          <select
            className="field__control"
            required
            value={toUserId}
            onChange={(e) => setToUserId(e.target.value)}
          >
            <option value="">Choose a member…</option>
            {candidates.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name ? `${member.name} — ${member.email}` : member.email}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">YOUR PASSWORD</span>
          <input
            className="field__control"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </label>
      </div>

      {error && <div className="login__error">{error}</div>}

      <div className="card__foot">
        Nothing moves yet. You will be shown exactly what changes and asked for your
        password once more — and the app only changes hands after that second step.
      </div>

      <div className="card__actions">
        <button className="button" type="submit" disabled={busy || !toUserId}>
          {busy && <Loader2 size={14} className="spin" />} Continue
        </button>
      </div>
    </form>
  );
}

/**
 * Who can reach this app, and with what.
 *
 * Reading the roster is open to any member; every editor here is hidden unless
 * the server said `canManage`. That is presentation only — the server refuses
 * the same calls regardless, so a member poking at the DOM gains nothing.
 */
export function TeamView() {
  const { activeApp, activeAppId, allApps, apps, refreshApps } = useWorkspace();

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  /** Keyed by appId: `{ [appId]: permissions }`. Absent means "not shared". */
  const [shares, setShares] = useState({});
  const [inviting, setInviting] = useState(false);

  // Only apps this account owns can be shared — the server refuses the rest,
  // so offering them here would only manufacture a 403.
  const ownedApps = useMemo(() => apps.filter((a) => a.isOwner), [apps]);

  const load = useCallback(async () => {
    if (!activeAppId) {
      setMembers([]);
      setInvites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await api.members.list(activeAppId);
      setMembers(data.members);
      setInvites(data.invites);
      setCanManage(data.canManage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeAppId]);

  useEffect(() => {
    load();
  }, [load]);

  // Opening the form starts from the app on screen, which is what someone who
  // clicked "Invite someone" on this page meant. The rest are one tick away.
  const openInvite = () => {
    setShares(activeAppId ? { [activeAppId]: { ...DEFAULT_PERMISSIONS } } : {});
    setInviteEmail('');
    setInviteOpen(true);
  };

  const toggleShare = (appId, on) => {
    setShares((current) => {
      const next = { ...current };
      if (on) next[appId] = { ...DEFAULT_PERMISSIONS };
      else delete next[appId];
      return next;
    });
  };

  const setSharePermission = (appId, scope, level) => {
    setShares((current) => ({
      ...current,
      [appId]: { ...current[appId], [scope]: level },
    }));
  };

  const sendInvite = async (event) => {
    event.preventDefault();
    setInviting(true);
    setError(null);
    setNotice(null);
    try {
      const grants = Object.entries(shares).map(([appId, permissions]) => ({
        appId,
        permissions,
      }));
      const result = await api.invites.create(inviteEmail, grants);
      setInviteOpen(false);
      setInviteEmail('');
      setShares({});

      const where =
        result.apps.length === 1
          ? result.apps[0].appName
          : `${result.apps.length} apps`;
      // The invite rows exist either way; saying so avoids the owner assuming
      // the person was emailed when the server could not send.
      setNotice(
        result.emailSent
          ? `Invitation sent to ${result.email} for ${where}.`
          : `Invitation created for ${result.email} (${where}), but the email could not be sent — check the server's Resend configuration.`
      );
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setInviting(false);
    }
  };

  const updateMember = async (userId, scope, level) => {
    setBusyId(userId);
    setError(null);
    try {
      const result = await api.members.update(activeAppId, userId, { [scope]: level });
      setMembers((current) =>
        current.map((m) => (m.userId === userId ? { ...m, permissions: result.permissions } : m))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const removeMember = async (member) => {
    if (!window.confirm(`Remove ${member.email} from ${activeApp?.name}? They lose access immediately.`)) {
      return;
    }
    setBusyId(member.userId);
    try {
      await api.members.remove(activeAppId, member.userId);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const revokeInvite = async (invite) => {
    setBusyId(invite.id);
    try {
      await api.members.revokeInvite(activeAppId, invite.id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  /** After a handover the caller is no longer the owner — reload both sides. */
  const afterTransfer = useCallback(async () => {
    await refreshApps();
    await load();
  }, [refreshApps, load]);

  if (!activeApp) {
    return (
      <div className="empty">
        {allApps
          ? 'Access is granted per app. Pick a single app in the switcher.'
          : 'Create an app before sharing access to it.'}
      </div>
    );
  }

  const shareCount = Object.keys(shares).length;

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">Team</h1>
        <span className="page__sub">Who can reach {activeApp.name}, and with what</span>
        {canManage && (
          <button
            type="button"
            className="button button--primary page__action"
            onClick={() => (inviteOpen ? setInviteOpen(false) : openInvite())}
          >
            <UserPlus size={13} /> Invite someone
          </button>
        )}
      </header>

      <div className="page__body page__body--narrow">
        {error && <div className="login__error">{error}</div>}
        {notice && <div className="result result--ok">{notice}</div>}

        {inviteOpen && (
          <form className="card" onSubmit={sendInvite}>
            <div className="card__head">
              <span className="card__title">Invite someone</span>
              <button
                type="button"
                className="icon-button card__close"
                onClick={() => setInviteOpen(false)}
                aria-label="Cancel"
              >
                <X size={13} />
              </button>
            </div>

            <div className="card__body">
              <label className="field">
                <span className="field__label">EMAIL ADDRESS</span>
                <input
                  className="field__control"
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@youragency.com"
                />
              </label>
            </div>

            <div className="share">
              <div className="share__head">
                <span className="share__title">Which apps?</span>
                <span className="share__hint">
                  One invitation, one email. Each app you tick carries its own access —
                  someone covering your inbox rarely needs your integration keys too.
                </span>
              </div>

              {ownedApps.map((app) => (
                <ShareApp
                  key={app.id}
                  app={app}
                  selected={Boolean(shares[app.id])}
                  permissions={shares[app.id] ?? DEFAULT_PERMISSIONS}
                  disabled={inviting}
                  onToggle={(on) => toggleShare(app.id, on)}
                  onChange={(scope, level) => setSharePermission(app.id, scope, level)}
                />
              ))}
            </div>

            <div className="card__foot">
              They get one link that expires in seven days and only works when signed in
              as that address — forwarding it grants nobody anything. You can change or
              revoke any of it afterwards, app by app.
            </div>

            <div className="card__actions">
              <button
                className="button button--primary"
                type="submit"
                disabled={inviting || shareCount === 0}
              >
                {inviting && <Loader2 size={14} className="spin" />}{' '}
                {shareCount > 1 ? `Send invitation (${shareCount} apps)` : 'Send invitation'}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="empty">
            <Loader2 size={16} className="spin" /> Loading the team…
          </div>
        ) : (
          <>
            {members.map((member) => (
              <div className="card" key={member.userId}>
                <div className="card__head">
                  <span className="card__title">
                    {member.isOwner && <Crown size={13} className="crown" />}
                    {member.name || member.email}
                  </span>
                  <span className="card__meta mono">{member.email}</span>
                  {canManage && !member.isOwner && (
                    <button
                      type="button"
                      className="icon-button is-danger"
                      onClick={() => removeMember(member)}
                      disabled={busyId === member.userId}
                      title="Remove from this app"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>

                {member.isOwner ? (
                  <div className="card__foot">
                    The owner always has full access to every area. That is deliberate — it is
                    what stops an app ending up with nobody able to administer it.
                  </div>
                ) : (
                  <>
                    {/* The same tick that granted this app in the invitation,
                        now showing what it granted. Unticking is the one
                        control that answers "take all of it away" without
                        making the owner walk down four ladders setting each to
                        none — and it is the same act as Remove, said in the
                        vocabulary of the form that gave the access. */}
                    <label className="share__pick share__pick--member">
                      <input
                        type="checkbox"
                        checked
                        disabled={!canManage || busyId === member.userId}
                        onChange={() => removeMember(member)}
                      />
                      <span className="share__name">Access to {activeApp.name}</span>
                      <span className="share__slug mono">{activeApp.slug}</span>
                    </label>

                    <div className="perms">
                      {Object.keys(PERMISSION_SCOPES).map((scope) => (
                        <PermissionPicker
                          key={scope}
                          scope={scope}
                          value={member.permissions[scope]}
                          disabled={!canManage || busyId === member.userId}
                          onChange={(level) => updateMember(member.userId, scope, level)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}

            {invites.length > 0 && (
              <div className="card">
                <div className="card__head">
                  <span className="card__title">
                    <Clock size={13} /> Pending invitations
                  </span>
                </div>
                <div className="settings">
                  {invites.map((invite) => (
                    <div className="settings__row" key={invite.id}>
                      <span>
                        <span className="mono">{invite.email}</span>
                        <span className="muted">
                          {' '}
                          — invited {shortTime(invite.createdAt)}, expires{' '}
                          {new Date(invite.expiresAt).toLocaleDateString()}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="button"
                        onClick={() => revokeInvite(invite)}
                        disabled={busyId === invite.id}
                      >
                        <MailWarning size={13} /> Revoke
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Strictly the real owner: an instance admin passes `canManage`
                but the transfer endpoints refuse them by design. */}
            {activeApp.isOwner && (
              <TransferCard app={activeApp} members={members} onDone={afterTransfer} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TeamView;
